import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';

const BrandAssetsContext = createContext(null);

/**
 * Loads all BrandAsset records on mount and exposes them via context.
 * Also injects favicon and OG image meta tags at runtime.
 *
 * Consumers:
 *  - Navbar/Footer: useBrandAsset('brand_logo') → returns file_url
 *  - RigDetail SimLogo: useSimLogo('iracing') → returns file_url
 *  - og_image is injected directly into <head> here
 *
 * The tab favicon is NOT handled here. It is owned by BrandFaviconManager at
 * the app root, so it covers the venue and admin routes as well, and so this
 * provider never overwrites the venue-only apple-touch-icon.
 */
export function BrandAssetsProvider({ children }) {
  const [assets, setAssets] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(() => {
    try {
      base44.entities.BrandAsset.list()
        .then((records) => { setAssets(records || []); setLoaded(true); })
        .catch(() => setLoaded(true));
    } catch {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    reload();
    let unsub;
    try {
      unsub = base44.entities.BrandAsset.subscribe(() => reload());
    } catch {
      /* entity unavailable — provider degrades gracefully */
    }
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [reload]);

  // Inject OG image
  useEffect(() => {
    const og = assets.find((a) => a.asset_type === 'og_image');
    if (!og?.file_url) return;
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[property="og:image"]', 'content', og.file_url);
    setMeta('meta[name="twitter:image"]', 'content', og.file_url);
  }, [assets]);

  const getAsset = useCallback((assetType) => {
    const rec = assets.find((a) => a.asset_type === assetType);
    return rec?.file_url || null;
  }, [assets]);

  const getSimLogo = useCallback((simKey) => {
    const rec = assets.find((a) => a.asset_type === 'sim_logo' && a.sim_key === simKey);
    return rec?.file_url || null;
  }, [assets]);

  const value = { assets, loaded, reload, getAsset, getSimLogo };

  return (
    <BrandAssetsContext.Provider value={value}>
      {children}
    </BrandAssetsContext.Provider>
  );
}

export function useBrandAssets() {
  const ctx = useContext(BrandAssetsContext);
  return ctx;
}

export function useBrandAsset(assetType) {
  const ctx = useContext(BrandAssetsContext);
  if (!ctx) return null;
  return ctx.getAsset(assetType);
}

export function useSimLogo(simKey) {
  const ctx = useContext(BrandAssetsContext);
  if (!ctx) return null;
  return ctx.getSimLogo(simKey);
}